import { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { DEVICES, DEVICE_IMG, MedconnectDevice, MedconnectDevicesConfig } from '@/constants/medconnect-devices-types'

import MedconnectDevicesDialog from '@/components/ui/dialogs/MedconnectDevicesDialog/MedconnectDevicesDialog'
import { MedconnectDeviceCardProps } from '@/components/ui/cards/MedconnectDevicesCard/MedconnectDeviceCard'
import { ExamSelectorDialog } from '@/components/ui/dialogs/MedconnectDevicesDialog/ExamSelectorDialog'

import { ExaminationTypes, getConfig, launchECG, launchSpirometry, restartDevice } from '@/core/classes/MedconnectDevicesAPI'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import {
  selectConsultationUpdateRequestTracker,
  sendBiological,
  sendMesuresSocketEvent,
  sendMesures,
  selectConsultationById,
} from '@/redux/reducers/consultation.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { Labels, Mesure } from '@/utils/medconnectDevices/constant'
import { DeviceProps, selectDevice } from '@/utils/medconnectDevices/devices'
import { isStaff } from '@/utils/type-guards'
import { buildDevicePatient } from '@/utils/medconnectDevices/patientBiometrics'

type MedconnectDevicesDialogContainerProps = {
  open: boolean
  onClose: () => void
  consultationId: string
}

function getOrderFromMesure(mesure: Mesure) {
  switch (mesure.label) {
    case Labels.NIBP:
      return 1
    case Labels.SPO2:
      return 2
    case Labels.BPM:
      return 3
    case Labels.TEMP:
      return 4
    case Labels.WEIGHT:
      return 5
    case Labels.GLYC:
      return 7
    case Labels.GLYC_2:
      return 8
    default:
      return 12
  }
}

export default function MedconnectDevicesDialogContainer(props: MedconnectDevicesDialogContainerProps) {
  const { open, onClose, consultationId } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useAppDispatch()

  const updateConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
    },
    onError: () => enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const { dispatch: sendBiologicalDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.consultation.biological.create.success'), { variant: 'success' })
    },
    onError: () => enqueueSnackbar(t('notification.consultation.biological.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const authUser = useAppSelector(selectAuthUser)
  const [config, setConfig] = useState<MedconnectDevicesConfig | undefined>(undefined)

  const [openExamSelector, setOpenExamSelector] = useState<boolean>(false)

  const handleSelectExam = (exam: ExaminationTypes) => {
    setOpenExamSelector(false)
    const patient = buildDevicePatient(consultation)
    if (!patient) return
    launchECG({
      patient,
      type: exam ? exam : ExaminationTypes.RestingECG,
    })
  }

  const consultation = useAppSelector((state) => selectConsultationById(state, consultationId))
  const consultationPatient = consultation?.patient

  const handleRestartDevice = async (id: string) => {
    const config = await restartDevice(id)
    if (!config) return
    setConfig(config)
  }

  useEffect(() => {
    const handleConfig = async () => {
      const config = await getConfig()
      if (!config) return
      setConfig(config)
    }
    handleConfig()
  }, [])

  const devices = useMemo(() => {
    const res: MedconnectDeviceCardProps[] = []
    // default devices
    Object.values(DEVICES).forEach((value) => {
      const emitData = (mesure: Mesure) => {
        if (consultationId && mesure.value !== '0')
          dispatch(
            sendMesuresSocketEvent({
              consultationId,
              mesures: [
                {
                  ...mesure,
                  order: getOrderFromMesure(mesure),
                },
              ],
            })
          )
      }
      const input: DeviceProps = {
        device: value,
        emitData,
        onError(error) {
          enqueueSnackbar(
            t('notification.device.error', {
              error: error,
            }),
            { variant: 'error' }
          )
        },
        onDisconnect() {
          enqueueSnackbar(t('notification.device.disconnect'), { variant: 'info' })
        },
        onConnect() {
          enqueueSnackbar(t('notification.device.connect'), { variant: 'success' })
        },
      }

      switch (value) {
        case DEVICES.PC303: {
          res.push({
            name: value,
            logoSrc: DEVICE_IMG[value],
            isLocal: false,
            connectDevice: () => {
              onClose()
              selectDevice({
                ...input,
                baudRate: isStaff(authUser) ? authUser.baudrate : 9600,
              })
            },
          })
          break
        }
        case DEVICES.TAIDOC_BGMETER: {
          const emitData = (mesure: Mesure) => {
            if (consultationId)
              sendBiologicalDispatch(
                sendBiological(consultationId, {
                  ...mesure,
                  comment: '',
                  files: []
                })
              )
          }
          res.push({
            name: value,
            logoSrc: DEVICE_IMG[value],
            isLocal: false,
            connectDevice: () => {
              onClose()
              selectDevice({
                ...input,
                emitData: emitData,
              })
            },
          })
          break
        }
        case DEVICES.THERMO_TAIDOC: {
          const emitData = (mesure: Mesure) => {
            if (consultationId)
              updateConsultationRequestHandler.dispatch(
                sendMesures(consultationId, [
                  {
                    ...mesure,
                    order: getOrderFromMesure(mesure),
                  },
                ])
              )
          }
          res.push({
            name: value,
            logoSrc: DEVICE_IMG[value],
            isLocal: false,
            connectDevice: () => {
              onClose()
              selectDevice({
                ...input,
                emitData: emitData,
              })
            },
          })
          break
        }
        case DEVICES.PM6100: {
          res.push({
            name: value,
            logoSrc: DEVICE_IMG[value],
            isLocal: false,
            connectDevice: () => {
              onClose()
              selectDevice(input)
            },
          })
          break
        }
        default: {
          res.push({
            name: value,
            logoSrc: DEVICE_IMG[value],
            isLocal: false,
            connectDevice: () => {
              onClose()
              selectDevice(input)
            },
          })
        }
      }
    })

    // installed devices
    if (!config?.Devices) return res

    const extra = config?.Devices.map((device: MedconnectDevice) => {
      if (device.name === DEVICES.SCHILLER_ECG) {
        if (!consultationPatient)
          return {
            ...device,
            logoSrc: DEVICE_IMG[device.name],
            isLocal: true,
            connectDevice: () => { },
          }
        return {
          ...device,
          logoSrc: DEVICE_IMG[device.name],
          isLocal: true,
          connectDevice: () => {
            setOpenExamSelector(true)
          },
        }
      } else if (device.name === DEVICES.MIR_SPIROMETRY) {
        if (!consultationPatient)
          return {
            ...device,
            logoSrc: DEVICE_IMG[device.name],
            isLocal: true,
            connectDevice: () => { },
          }
        return {
          ...device,
          logoSrc: DEVICE_IMG[device.name],
          isLocal: true,
          connectDevice: () => {
            const patient = buildDevicePatient(consultation)
            if (!patient) return
            onClose()
            launchSpirometry({
              patient,
            })
          },
        }
      } else {
        return {
          ...device,
          logoSrc: DEVICE_IMG[device.name],
          isLocal: true,
          connectDevice: () => {
            handleRestartDevice(device._id)
          },
        }
      }
    })
    var noDouble: MedconnectDeviceCardProps[] = []
    // if a device exists in extra and res, then remove it from res
    extra.forEach((device) => {
      noDouble = res.filter((d) => d.name !== device.name)
    })
    // returns the array extra and noDouble merged
    return [...extra, ...noDouble]
  }, [consultationId, dispatch, updateConsultationRequestHandler, onClose, sendBiologicalDispatch, t, enqueueSnackbar, authUser, config, consultation, consultationPatient])
  return (
    <>
      <MedconnectDevicesDialog open={open} onClose={onClose} devices={devices} />
      <ExamSelectorDialog open={openExamSelector} onClose={() => setOpenExamSelector(false)} onExamSelected={handleSelectExam} />
    </>
  )
}
